import futuraSpec from 'utils/futuraSpec.json';
import { CollectionAttributes, CollectionFilterItem, Item, OwnedNft } from './types';
import { FUTURA_ATTRIBUTES, FUTURA_PALETTES, FUTURA_EFFECT_ATTRIBUTES } from '../constants';
import pulseIcon from 'img/pulse.svg';
import shakeIcon from 'img/shake.svg';
import lightIcon from 'img/light.svg';
import orbitIcon from 'img/orbit.svg';
import spheresIcon from 'img/spheres.svg';
import waveIcon from 'img/wave.svg';
import laserIcon from 'img/laser.svg';
import lightningIcon from 'img/lightning.svg';
import { find, groupBy, sortBy } from 'lodash';
import bassIcon from 'img/bass.svg';
import synthIcon from 'img/synth.svg';
import drumIcon from 'img/drum.svg';
import raiserIcon from 'img/raiser.svg';
import fxIcon from 'img/fx.svg';
import Color from 'color';
import max from 'lodash/max';

const EBF = window.EulerBeatsFutura;
interface FuturaEffect {
  name: string;
  icon: string;
  origin?: string;
}

export const allEffects = [
  {
    name: FUTURA_ATTRIBUTES.lightPulseEffect,
    icon: pulseIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.shakingEffect,
    icon: shakeIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.lightFlickeringEffect,
    icon: lightIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.orbitingParticlesEffect,
    icon: orbitIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.spheresEffect,
    icon: spheresIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.lightWavesEffect,
    icon: waveIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.laserEffect,
    icon: laserIcon,
  },
  {
    name: FUTURA_ATTRIBUTES.lightningEffect,
    icon: lightningIcon,
  },
];

export const FUTURA_CLIP_LAYER_ICONS = {
  Lead: synthIcon,
  Backing: bassIcon,
  Drums: drumIcon,
  Ambience: raiserIcon,
  FX: fxIcon,
};

// TODO:type out attributes
export function getEffectsList(attributes: any) {
  let availableEffects: FuturaEffect[] = [];
  allEffects.forEach(effect => {
    if (attributes[effect.name] === 'true' && availableEffects.indexOf(effect) === -1) {
      availableEffects.push(effect);
    }
  });
  return availableEffects;
}

export const getMixCodeFromAttributes = (attributes: {}) => {
  let code: string[] = [];
  for (let layer of futuraSpec.layers) {
    for (let section of futuraSpec.sections) {
      const attrName = `${section.name} ${layer.name}`;
      const value = attributes[attrName];
      const id = futuraSpec.variants.find(variant => variant.name === value)?.id || '0';
      code.push(id);
    }
  }

  return code.join(',');
};

export const getMixCodeFromRemixForm = (values, mixes: OwnedNft[]) => {
  let code: number[] = [];
  for (let layer of futuraSpec.layers) {
    for (let section of futuraSpec.sections) {
      const sectionName = `${section.name} ${layer.name}`;
      const itemId = values[sectionName] === 'A' ? values.mixA : values.mixB;
      const mix = mixes.find(item => item.token_id === itemId);
      const clipName = mix?.metadata?.attributes[sectionName];
      const clipId = clipNameToId(clipName) || '0';
      code.push(parseInt(clipId));
    }
  }

  return code;
};

export const sortAttributes = (attributes: CollectionAttributes[]): CollectionAttributes[] => {
  const basicAttr = Object.values(FUTURA_ATTRIBUTES);
  let sortedAttributes: CollectionAttributes[] = [];
  const keys: string[] = [];
  // add basic attributes
  for (let attrName of basicAttr) {
    const attribute = attributes.find(a => a.name === attrName);
    if (attribute) {
      sortedAttributes.push(attribute);
      keys.push(attribute.name);
    }
  }
  // add cells
  for (let section of futuraSpec.sections) {
    for (let layer of futuraSpec.layers) {
      const attrName = `${section.name} ${layer.name}`;
      const attribute = attributes.find(a => a.name === attrName);
      if (attribute) {
        sortedAttributes.push(attribute);
        keys.push(attribute.name);
      }
    }
  }

  // add remaining attributes
  for (let attribute of attributes) {
    if (keys.indexOf(attribute.name) === -1) {
      sortedAttributes.push(attribute);
    }
  }

  return sortedAttributes;
};

export const filterItems = (items: Item[], attributes) => {
  const groupedByName = groupBy(attributes, attr => attr.name);
  return items.filter(item =>
    Object.values(groupedByName).reduce(
      (acc, values) =>
        acc &&
        values.reduce(
          (acc2, attrValue) => acc2 || item.attributes[attrValue.name] === attrValue.value,
          false
        ),
      true
    )
  );
};

export const mapToCollectionFilterItems = (
  attributes: CollectionAttributes[]
): Array<CollectionFilterItem> => {
  const sortedAttributes = sortAttributes(attributes);

  // Contains all futura effect attribute names but in lower casing.
  const futuraEffectsLowerCase = Object.values(FUTURA_EFFECT_ATTRIBUTES)
    .map(key => {
      return key ? key.toLowerCase() : '';
    })
    .filter(key => !!key);

  // These will be used to generate hierarchies for clips.
  const clipsHierarchyKeys = [
    { name: 'Intro', regex: /^Intro [A-z]+/ },
    { name: 'Verse', regex: /^Verse [A-z]+/ },
    { name: 'Buildup 1', regex: /^Buildup 1 [A-z]+/ },
    { name: 'Bridge', regex: /^Bridge [A-z]+/ },
    { name: 'Buildup 2', regex: /^Buildup 2 [A-z]+/ },
    { name: 'Drop', regex: /^Drop [A-z]+/ },
  ];

  // Most attributes can be mapped 1:1 as a filter item and will be rendered as a single accordeon component.
  const flatHierarchyCollectionFilterItems = sortedAttributes
    .filter((collectionAttribute: CollectionAttributes) => {
      return (
        collectionAttribute &&
        collectionAttribute.name &&
        // Attributes related to visual effects have their own hierarchy.
        futuraEffectsLowerCase.indexOf(collectionAttribute.name.toLowerCase()) === -1 &&
        // Attributes related to clips have their own hierarchy.
        !Boolean(
          find(clipsHierarchyKeys, (clipKey: { name: string; regex: RegExp }) => {
            return clipKey && clipKey.regex && clipKey.regex.test(collectionAttribute.name);
          })
        )
      );
    })
    .map((collectionAttribute: CollectionAttributes): CollectionFilterItem => {
      return {
        name: collectionAttribute.name,
        attributes: attributes.filter((attribute: CollectionAttributes) => {
          return (
            attribute &&
            attribute.name &&
            collectionAttribute.name &&
            attribute.name.toLowerCase() === collectionAttribute.name.toLowerCase()
          );
        }),
      };
    });

  // The visual attributes should be seen as a group and each of them will be rendered as nested accordeon components.
  const visualEffectsCollectionFilterItem = {
    name: 'Visual Effects',
    attributes: attributes.filter((attribute: CollectionAttributes) => {
      return (
        attribute &&
        attribute.name &&
        futuraEffectsLowerCase.indexOf(attribute.name.toLowerCase()) !== -1
      );
    }),
  };

  // Merge all filter items into 1 result set.
  const unsortedCollectionFilterItems: any = [
    ...flatHierarchyCollectionFilterItems,
    visualEffectsCollectionFilterItem,
    ...clipsHierarchyKeys.map((clipKey: { name: string; regex: RegExp }) => {
      return {
        name: clipKey.name,
        attributes: sortBy(
          attributes.filter((attribute: CollectionAttributes) => {
            return attribute && attribute.name && clipKey.regex.test(attribute.name);
          }),
          (attribute: CollectionAttributes) => sortedAttributes.indexOf(attribute)
        ),
      };
    }),
  ];

  // Resort
  const sortedCollectionFilterItems: Array<CollectionFilterItem> =
    [] as Array<CollectionFilterItem>;
  sortedAttributes.forEach((attribute: CollectionAttributes) => {
    if (!attribute || !attribute.name) return;

    const match: CollectionFilterItem = unsortedCollectionFilterItems.find(
      (collectionFilterItem: CollectionFilterItem) => {
        return (
          collectionFilterItem &&
          collectionFilterItem.attributes &&
          collectionFilterItem.attributes.indexOf(attribute) !== -1
        );
      }
    );

    if (match && sortedCollectionFilterItems.indexOf(match) === -1)
      sortedCollectionFilterItems.push(match);
  });

  return sortedCollectionFilterItems;
};

// Helper to get the correct icon based on the description of an attribute.
export const getFuturaAttributeIcon = (attributeName: string): string | undefined => {
  // Initialize return variable
  let icon: string | undefined = '';

  // Preliminary null check.
  if (!attributeName) return icon;

  // Option #1: Is the attribute associated with an effect icon?
  const matchingEffect = allEffects.find(futuraEffect => {
    return (
      futuraEffect &&
      futuraEffect.name &&
      attributeName.toLowerCase() === futuraEffect.name.toLowerCase()
    );
  });
  icon = matchingEffect ? matchingEffect.icon : undefined;

  // Option #2: Is the attribute associated with a clip layer icon?
  if (!icon) {
    const matchingClipLayerKey = Object.keys(FUTURA_CLIP_LAYER_ICONS).find((layerKey: string) => {
      return layerKey && attributeName.toLowerCase().includes(layerKey.toLowerCase());
    });
    if (matchingClipLayerKey) {
      icon = FUTURA_CLIP_LAYER_ICONS[matchingClipLayerKey];
    }
  }

  return icon;
};

export const getMixPieceData = (attributes = {}) => {
  const tempo = attributes[FUTURA_ATTRIBUTES.bpm];
  const mixCode = getMixCodeFromAttributes(attributes);
  return getPieceDataFromTempoAndMixCode(tempo, mixCode);
};

export const getPieceDataFromTempoAndMixCode = (tempo: number, mixCode: string) => {
  const pieceSpec = EBF.utils.updatePieceSpecRaw(futuraSpec);
  const pieceData = EBF.utils.buildPieceData({
    pieceSpec,
    mixCode,
    tempo,
  });
  return pieceData;
};

export const getColorVariant = (color: string, index: number) => {
  return Color(color)
    .rotate(index * 2)
    .hex();
};

export interface RemixData {
  generation: number;
  twists: number;
  distortion: boolean;
  paletteName: string;
  noteShape: string;
  effects: FuturaEffect[];
}

export const simulateRemix = (
  mixA: any,
  mixB: any,
  selectedMixCode: number[]
): RemixData | undefined => {
  if (!mixA || !mixB) {
    return undefined;
  }
  const genA = mixA[FUTURA_ATTRIBUTES.generation];
  const genB = mixB[FUTURA_ATTRIBUTES.generation];
  const remixGen = Math.max(parseInt(genA), parseInt(genB)) + 1;
  const twists = 2 * remixGen + 1;

  const traitsCount = {
    palette: {},
    noteShape: {},
  };
  let effects: FuturaEffect[] = [];
  let hasDistortion = false;

  const selectedVariants = futuraSpec.variants.filter(
    variant => selectedMixCode.indexOf(parseInt(variant.id)) !== -1
  );

  for (let variant of selectedVariants) {
    const { traits } = variant;

    if (traits.find(t => t.name === 'Distortion' && t.value === 'true')) {
      hasDistortion = true;
    }

    for (let effect of allEffects) {
      if (
        traits.find(t => t.name === 'Effect' && t.value === effect.name) &&
        effects.find(e => e.name === effect.name) === undefined
      ) {
        effects.push({
          ...effect,
          origin: variant.name,
        });
      }
    }

    const palette = traits.find(t => t.name === 'ColorPalette')?.value;
    if (palette) {
      traitsCount.palette[palette] = traitsCount.palette[palette] + 1 || 1;
    }
    const noteShapeTrait = traits.find(t => t.name === 'NoteShape')?.value;
    if (noteShapeTrait) {
      traitsCount.noteShape[noteShapeTrait] = traitsCount.noteShape[noteShapeTrait] + 1 || 1;
    }
  }

  const paletteCounters = {};
  let maxPaletteIndex = '-';
  for (let mixCode of selectedMixCode) {
    const variant = selectedVariants.find(v => v.id === mixCode.toString());
    const palette = variant?.traits.find(t => t.name === 'ColorPalette')?.value;
    if (palette) {
      paletteCounters[palette] = paletteCounters[palette] + 1 || 1;
      if (maxPaletteIndex === '-' || paletteCounters[palette] > paletteCounters[maxPaletteIndex]) {
        maxPaletteIndex = palette;
      }
    }
  }
  const paletteName = FUTURA_PALETTES[maxPaletteIndex]?.name;

  const maxNoteShape = max(Object.values(traitsCount.noteShape));
  const maxNoteShapeKey =
    Object.keys(traitsCount.noteShape).find(key => traitsCount.noteShape[key] === maxNoteShape) ||
    0;
  const noteShape =
    {
      0: 'Square',
      1: 'Line',
      2: 'Triangle',
    }[maxNoteShapeKey] || 'Square';

  return {
    twists: twists > 5 ? 5 : twists,
    generation: remixGen,
    distortion: hasDistortion,
    paletteName,
    noteShape,
    effects,
  };
};

export const clipNameToId = (clipName: string): string | undefined =>
  futuraSpec.variants.find(v => v.name === clipName)?.id;
